import { observer } from "mobx-react-lite"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { dollarsPrettified } from "@/domains/library"
import { SCALE } from "@/domains/tokens/static-tokens"

/**
 * Observer ensures totals recalculate when async data arrives (Lock, MinimalPools, ClFarms,
 * LockRewards). Each observable read (e.g. pool.gaugeUserBalanceValueUSD) establishes
 * a dependency so MobX triggers a re-render when repositories update.
 */
export const DashboardGrandTotal = observer(function DashboardGrandTotal() {
  const scaleApp = useScaleApplication()

  // 1. veSCALE Total Value: locked SCALE * price
  const locks = scaleApp.Lock.Repository.getAll()
  const userLocked = locks.reduce((acc, nft) => acc + Number(nft.lockAmount), 0)
  const veScaleTotalValue = userLocked * SCALE.bestPrice

  // 2. Classic Pools Total Staked
  const allPools = scaleApp.MinimalPools.Repository.getAll()
  const poolsWithRewards = scaleApp.MinimalPools.Repository.getAllWithGaugeRewards()
  const classicPoolsWithPositions = allPools.filter((pool) => {
    if (!pool.hasGauge) return false
    const hasStaked =
      pool.onChainUserGaugeBalance > 0n || pool.onChainUserGaugeReserve0 > 0n || pool.onChainUserGaugeReserve1 > 0n
    const hasUnstaked = pool.onChainUserBalance > 0n
    return hasStaked || hasUnstaked
  })
  const classicPoolsStaked = classicPoolsWithPositions.filter(
    (pool) =>
      pool.onChainUserGaugeBalance > 0n || pool.onChainUserGaugeReserve0 > 0n || pool.onChainUserGaugeReserve1 > 0n,
  )
  const classicTotalStaked = classicPoolsStaked.reduce((sum, pool) => sum + pool.gaugeUserBalanceValueUSD, 0)

  // Classic Pools Unclaimed Rewards
  const classicTotalEarned = poolsWithRewards.reduce((sum, pool) => {
    const rewardAmount = SCALE.formatUnits(pool.onChainUserGaugeGovernanceEarned)
    return sum + Number(rewardAmount)
  }, 0)
  const classicUnclaimedRewardsUSD = SCALE.calculateValueUSD(classicTotalEarned)

  // 3. CL Farms Total Deposited (staked + unstaked)
  const allClFarms = scaleApp.ClFarms.Repository.getAll()
  const clFarmsWithRewards = scaleApp.ClFarms.Repository.getAllWithGaugeRewards()
  const clFarmsWithPositions = allClFarms.filter((farm) => {
    if (!farm.gaugeAddress) return false
    const hasStaked =
      farm.onChainUserGaugeBalance > 0n || farm.onChainUserGaugeReserve0 > 0n || farm.onChainUserGaugeReserve1 > 0n
    const hasUnstaked = farm.onChainUserBalance > 0n
    return hasStaked || hasUnstaked
  })
  const clTotalDeposited = clFarmsWithPositions.reduce((sum, farm) => {
    return sum + farm.gaugeUserBalanceValueUSD + farm.userBalanceValueUSD
  }, 0)

  // CL Farms Unclaimed Rewards
  const clTotalEarned = clFarmsWithRewards.reduce((sum, farm) => {
    const rewardAmount = SCALE.formatUnits(farm.onChainUserGaugeGovernanceEarned)
    return sum + Number(rewardAmount)
  }, 0)
  const clUnclaimedRewardsUSD = SCALE.calculateValueUSD(clTotalEarned)

  // 4. Earned Fees Total in Wallet (unstaked LP only)
  const feePools = allPools.filter(
    (pool) =>
      pool.onChainUserBalance > 0n || pool.onChainUserClaimableFees0 > 0n || pool.onChainUserClaimableFees1 > 0n,
  )
  const poolsWithUnstakedDeposits = feePools.filter((pool) => pool.onChainUserBalance > 0n)
  const earnedFeesTotalInWallet = poolsWithUnstakedDeposits.reduce((sum, pool) => sum + pool.userBalanceValueUSD, 0)

  // Earned Fees Unclaimed
  const earnedFeesUnclaimedUSD = feePools.reduce((sum, pool) => {
    const fees0Amount = Number(pool.token0.formatUnits(pool.onChainUserClaimableFees0))
    const fees1Amount = Number(pool.token1.formatUnits(pool.onChainUserClaimableFees1))
    const fees0USD = pool.token0.calculateValueUSD(fees0Amount)
    const fees1USD = pool.token1.calculateValueUSD(fees1Amount)
    return sum + fees0USD + fees1USD
  }, 0)

  // 5. Manual CL positions and claimable fees
  const manualClPositions = [
    ...scaleApp.ManualClV1.Positions.activePositions,
    ...scaleApp.ManualClV2.Positions.activePositions,
  ]
  const manualClPositionValue = manualClPositions.reduce((sum, position) => sum + position.positionValueUsd, 0)
  const manualClUnclaimedFeesUSD = manualClPositions.reduce((sum, position) => sum + position.claimableFeesUsd, 0)

  const totalValue =
    veScaleTotalValue + classicTotalStaked + clTotalDeposited + earnedFeesTotalInWallet + manualClPositionValue

  const votingRewardsUSD = scaleApp.LockRewards.Repository.totalRewardsValueUSD
  const totalUnclaimedRewards =
    votingRewardsUSD +
    classicUnclaimedRewardsUSD +
    clUnclaimedRewardsUSD +
    earnedFeesUnclaimedUSD +
    manualClUnclaimedFeesUSD

  return (
    <div className="mb-4 flex flex-col gap-4 rounded-2xl border border-p400-light bg-p400 px-6 py-5 shadow-2xl shadow-black/10 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-t300">Portfolio Overview</span>
        <p className="mt-1 text-sm text-t200">Estimated value and rewards across connected Scale positions.</p>
      </div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex flex-col gap-0.5 sm:items-end">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-t300">Total Value</span>
          <span className="text-2xl font-bold tracking-tight text-t100">
            <span className="mr-[2px] text-t300">~</span>
            {dollarsPrettified(totalValue, 2)}
          </span>
        </div>
        <div className="flex flex-col gap-0.5 sm:items-end">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-t300">Unclaimed Rewards</span>
          <span className="text-2xl font-bold tracking-tight text-t100">
            <span className="mr-[2px] text-t300">~</span>
            {dollarsPrettified(totalUnclaimedRewards, 2)}
          </span>
        </div>
      </div>
    </div>
  )
})
